import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import styles from './LanguagePicker.module.scss';
import { Button, Icon } from 'ui/components/1-atoms';
import { Modal } from 'ui/components/3-organisms/Modal';
import IconChevron from 'ui/icons/icon-chevron.svg';
import { useRouter } from 'next/router';

type Country = { name: string; code: string; cultures: string[] };
type ContinentProps = { continentName: string; countries: Array<{ properties?: any } & any> };

export interface LanguagePickerProps {
	className?: string;
	continentsData: ContinentProps[];
	continentsRaw?: Umbraco.Continent[];
	modalTitle?: string;
	dictionary?: { [key: string]: string };
	handleCountrySelection?: (language: string, country: string) => void;
}

const TOP_LANGS = [
	{ culture: 'en', name: 'English' },
	{ culture: 'de', name: 'Deutsch' },
	{ culture: 'ja', name: '日本語' },
	{ culture: 'zh', name: '中文' },
	{ culture: 'fr', name: 'Français' },
	{ culture: 'es', name: 'Español' },
	{ culture: 'it', name: 'Italiano' },
] as const;

const DEFAULT_CULTURE = 'en';
const localeHrefFor = (culture: string) => (culture === DEFAULT_CULTURE ? '/' : `/${culture}/`);

function flattenCountries(data: ContinentProps[]): Country[] {
	const byCode = new Map<string, Country>();
	for (const cont of data ?? []) {
		for (const item of cont.countries ?? []) {
			const p = item.properties ?? item;
			const name = p?.countryName;
			const code = p?.countryCode;
			const raw = p?.cultureCode;
			const cultures = Array.isArray(raw) ? raw : typeof raw === 'string' ? [raw] : [];
			if (!name || !code) continue;

			const existing = byCode.get(code);
			if (existing) existing.cultures = [...new Set([...existing.cultures, ...cultures])];
			else byCode.set(code, { name, code, cultures });
		}
	}
	return [...byCode.values()].sort((a, b) => a.name.localeCompare(b.name, 'en'));
}

const makeLanguageLabelResolver = () => {
	let intl: Intl.DisplayNames | null = null;
	try {
		intl = new (Intl as any).DisplayNames(['en'], { type: 'language' });
	} catch {
		intl = null;
	}
	return (code?: string | null) => {
		const c = String(code || '').toLowerCase();
		if (!c) return 'English';
		const base = c.split('-')[0];

		const top = TOP_LANGS.find((l) => l.culture === base)?.name;
		if (top) return top;

		const viaIntl = intl?.of?.(base);
		if (viaIntl) return viaIntl;

		return base.toUpperCase();
	};
};

export const LanguagePicker: React.FC<LanguagePickerProps> = ({
	className,
	continentsData,
	continentsRaw,
	modalTitle,
	handleCountrySelection,
	dictionary,
}) => {
	const router = useRouter();
	const resolveLabel = useMemo(makeLanguageLabelResolver, []);

	const countries = useMemo(() => flattenCountries(continentsData ?? []), [continentsData]);

	const availableCultures = useMemo(() => {
		const s = new Set<string>();
		for (const c of countries) for (const cul of c.cultures) s.add(String(cul).toLowerCase());
		s.add(DEFAULT_CULTURE);
		return s;
	}, [countries]);

	const topLangs = useMemo(() => TOP_LANGS.filter((l) => availableCultures.has(l.culture)), [availableCultures]);
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const handleCloseModal = () => setIsModalOpen(false);
	const detectCultureFromPath = useCallback(
		(path: string): string => {
			const clean = path.split('?')[0].split('#')[0];
			const firstSeg = clean.split('/').filter(Boolean)[0];
			if (firstSeg && availableCultures.has(firstSeg.toLowerCase())) return firstSeg.toLowerCase();
			return DEFAULT_CULTURE;
		},
		[availableCultures],
	);

	const initialCulture = (() => {
		const rLocale = (router as any)?.locale as string | undefined;
		if (rLocale) return rLocale.toLowerCase();
		if (typeof window !== 'undefined') {
			const path = window.location.pathname.split('?')[0].split('#')[0];
			const firstSeg = path.split('/').filter(Boolean)[0];
			return (firstSeg || DEFAULT_CULTURE).toLowerCase();
		}
		return DEFAULT_CULTURE;
	})();
	const [triggerLabel, setTriggerLabel] = useState<string>(() => resolveLabel(initialCulture));

	useEffect(() => {
		const culture = detectCultureFromPath(router.asPath || '/');
		setTriggerLabel(resolveLabel(initialCulture));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [router.asPath, detectCultureFromPath]);

	useEffect(() => {
		const handler = (url: string) => {
			const culture = detectCultureFromPath(url);
			setTriggerLabel(resolveLabel(initialCulture));
		};
		router.events?.on?.('routeChangeComplete', handler);
		return () => {
			router.events?.off?.('routeChangeComplete', handler);
		};
	}, [router.events, detectCultureFromPath, resolveLabel]);

	useEffect(() => {
		if (!open) return;
		const onDocClick = (e: MouseEvent) => {
			if (!rootRef.current) return;
			if (!rootRef.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onDocClick);
		return () => document.removeEventListener('mousedown', onDocClick);
	}, [open]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false);
		};
		document.addEventListener('keydown', onKey);
		return () => document.removeEventListener('keydown', onKey);
	}, [open]);

	useEffect(() => {
		const body = document.querySelector('body') as HTMLBodyElement | null;
		if (!body) return;
		body.style.overflow = isModalOpen ? 'hidden' : '';
	}, [isModalOpen]);

	const onPick = () => setOpen(false);
	const dropdownId = 'languagepicker-dropdown';

	const switchLang = useCallback(
		(culture: string, nameHint?: string) => {
			setTriggerLabel(nameHint || resolveLabel(culture));
			setOpen(false);
			window.dispatchEvent(new CustomEvent('nav:languagePicked'));

			const canUseLocaleSwitch = Array.isArray((router as any).locales) && ((router as any).locales as string[]).includes(culture);

			if (canUseLocaleSwitch) {
				router.push(router.asPath, router.asPath, { locale: culture, scroll: false });
			} else {
				router.push(localeHrefFor(culture));
			}
		},
		[router, resolveLabel],
	);

	const handleModalClick = useCallback(
		(language: string, country: string) => {
			setTriggerLabel(resolveLabel(language));
			handleCountrySelection?.(language, country);
			setIsModalOpen(false);

			window.dispatchEvent(new CustomEvent('nav:languagePicked'));

			const canUseLocaleSwitch = Array.isArray((router as any).locales) && ((router as any).locales as string[]).includes(language);

			if (canUseLocaleSwitch) {
				router.push(router.asPath, router.asPath, { locale: language, scroll: false });
			} else {
				router.push(localeHrefFor(language));
			}
		},
		[handleCountrySelection, resolveLabel, router],
	);

	return (
		<div ref={rootRef} className={classNames(styles.LanguagePicker, className)}>
			<div className={styles.LanguagePicker_languageHeader}>
				<Button
					type="button"
					className={styles.LanguagePicker_languageTrigger}
					aria-expanded={open}
					aria-controls={dropdownId}
					onClick={() => setOpen((p) => !p)}
				>
					{triggerLabel}
				</Button>
				<Icon className={`${styles.LanguagePicker_languageIcon} ${open ? styles.isOpenIcon : ''}`}>
					<IconChevron />
				</Icon>
			</div>

			<ul id={dropdownId} className={classNames(styles.LanguagePicker_languageDropdown)} hidden={!open}>
				{/* Top languages */}
				{topLangs.map((l) => (
					<li key={`lang-${l.culture}`} className={styles.LanguagePicker_item}>
						<button
							type="button"
							className={styles.LanguagePicker_link}
							data-tracking-name={l.culture}
							onClick={() => switchLang(l.culture, l.name)}
							aria-label={`Switch language to ${l.name}`}
						>
							{l.name}
						</button>
					</li>
				))}

				<li className={styles.LanguagePicker_item}>
					<Button
						type="button"
						className={classNames(styles.LanguagePicker_link, styles.LanguagePicker_more)}
						onClick={() => {
							setOpen(false);
							setIsModalOpen(true);
						}}
					>
						{dictionary?.moreLanguagesText}
					</Button>
				</li>
			</ul>

			{isModalOpen && continentsRaw && (
				<Modal
					title={modalTitle}
					modalType="languageSelectorModal"
					ariaClose="close"
					handleClose={() => setIsModalOpen(false)}
					handleClick={handleModalClick}
					continents={continentsRaw}
				/>
			)}
		</div>
	);
};
